import PropTypes from "prop-types";
import React from "react";

import { useDeviceInfo } from "@swa-ui/browser";
import { PageGrid } from "@swa-ui/core";
import {
  CalendarFormField,
  Form,
  RadioButtonFormField,
  RequiredIndicator,
  SubmitFormField,
  TextFormField,
} from "@swa-ui/form";
import {
  AutocompleteStationsFormField,
  AutocompleteVacationDestinationsFormField,
} from "@swa-ui/itinerary";
import i18n from "@swa-ui/locale";

import { FLIGHT_CAR, FLIGHT_HOTEL, FLIGHT_HOTEL_CAR } from "../defines/packageType";
import { useVacationsFormLogic } from "../useVacationsFormLogic";
import { TravelerSelector } from "./TravelerSelector";
import styles from "./VacationsBookingFormSlim.module.scss";

export const VacationsBookingFormSlim = (props) => {
  const { defaultFormDataOverride = {} } = props;
  const { isSmallOrMedium } = useDeviceInfo();
  const { defaultSubmitButtonText, formProps } = useVacationsFormLogic({
    defaultFormDataOverride,
  });
  const {
    departureDateProps,
    destinationAirportCodeProps,
    formComponentProps,
    originationAirportCodeProps,
    packageOptionsProps,
    promoCodeProps,
    returnDateProps,
    submitButtonProps,
    travelerSelectorProps,
  } = formProps;

  return (
    <PageGrid className={styles.layout}>
      <Form {...formComponentProps}>
        <RequiredIndicator className={styles.requiredIndicator} />
        <RadioButtonFormField {...getPackageRadioOptionProps()} />
        <AutocompleteStationsFormField {...getOriginationFormFieldProps()} />
        <AutocompleteVacationDestinationsFormField {...getDestinationFormFieldProps()} />
        <CalendarFormField className={styles.departureDate} {...getDepartureDateFormFieldProps()} />
        <CalendarFormField className={styles.returnDate} {...getReturnDateFormFieldProps()} />
        <TravelerSelector className={styles.travelers} {...travelerSelectorProps} dropdown="true" />
        <TextFormField className={styles.promo} {...getEnterPromoFormFieldProps()} />
        <div className={styles.submit}>
          <SubmitFormField className={styles.submitButton} {...submitButtonProps}>
            {defaultSubmitButtonText}
          </SubmitFormField>
        </div>
      </Form>
    </PageGrid>
  );

  function getPackageRadioOptionProps() {
    const { name, onChange, value } = packageOptionsProps;

    return {
      className: styles.package,
      componentProps: {
        ariaLiveInput: (option, index, length) =>
          `${i18n("VacationsBookingFormSlim__CHOOSE_PACKAGE_RADIO_BUTTON_ARIA_LIVE", {
            index,
            length,
            option,
          })}`,
        defaultValue: value,
        direction: isSmallOrMedium ? "column" : "row",
        onChange,
        radioButtonInfoList: [
          {
            label: i18n("VacationsBookingFormSlim__FLIGHT_HOTEL"),
            value: FLIGHT_HOTEL,
          },
          {
            label: i18n("VacationsBookingFormSlim__FLIGHT_HOTEL_CAR"),
            value: FLIGHT_HOTEL_CAR,
          },
          {
            label: i18n("VacationsBookingFormSlim__FLIGHT_CAR"),
            value: FLIGHT_CAR,
          },
        ],
      },
      hideBottomSection: true,
      label: i18n("VacationsBookingFormSlim__CHOOSE_PACKAGE_LABEL"),
      name,
    };
  }

  // TODO: Include an asterisk (*) in Nextgen required fields. Update form component (@swa-ui/form) to handle this automatically (PEGMON-789).
  function getOriginationFormFieldProps() {
    return {
      ...originationAirportCodeProps,
      className: styles.originationAirportCode,
      label: `${i18n("VacationsBookingFormSlim__FROM")}*`,
    };
  }

  function getDestinationFormFieldProps() {
    return {
      ...destinationAirportCodeProps,
      className: styles.destinationAirportCode,
      label: `${i18n("VacationsBookingFormSlim__TO")}*`,
    };
  }

  function getDepartureDateFormFieldProps() {
    return {
      ...departureDateProps,
      label: `${i18n("VacationsBookingFormSlim__DEPART_DATE")}*`,
    };
  }

  function getReturnDateFormFieldProps() {
    return {
      ...returnDateProps,
      label: `${i18n("VacationsBookingFormSlim__RETURN_DATE")}*`,
    };
  }

  function getEnterPromoFormFieldProps() {
    return {
      ...promoCodeProps,
      label: i18n("VacationsBookingFormSlim__PROMO_CODE"),
    };
  }
};

VacationsBookingFormSlim.propTypes = {
  /** Consumers of this component can optionally set default values for form fields using this prop.
   * For example, a static page driven by WCM data could use this prop. */
  defaultFormDataOverride: PropTypes.object,
};
